import CustomDialogBox from '@/Components/Common/CustomDailogBox';
import Error from '@/Components/Common/Error';
import InfoIcon from '@/Components/Common/Icons/InformationIcon.svg';
import useApplyInsightRule from '@/Components/Hooks/useApplyInsightRule';
import useDeleteInsightRule from '@/Components/Hooks/useDeleteInsightRule';
import useGetInsightRules from '@/Components/Hooks/useGetInsightRules';
import useInsightRuleSchemaMetadata from '@/Components/Hooks/useInsightRuleSchemaMetadata';
import {
  ACCESSORIES_PROPERTIES,
  DEVICE_CONNECTION_STATUS,
  HC200_MODEL_NUMBER,
  WELLNESS_HUB_DEVICE_TYPE,
} from '@/constants/device';
import { getConfig, translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { ConfigSourceKeys } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { message, Skeleton } from 'antd';
import { cloneDeep, get, isEmpty, set, startCase, uniq } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { DashboardIconAdd } from '../icons';
import RuleFormCard from './RuleFormCard';
import {
  AddRuleButton,
  MessageToastContent,
  NoRulesDiv,
  RulesListContainer,
  SkeletonWrapper,
} from './Rules.styles';
import {
  CATEGORY_PRODUCTCLASS_MAPPING,
  createCondition,
  createRuleInitialValues,
  findTemplateByName,
  findTemplateByThreshold,
  formatUIJsonToComponentState,
  getConditionPayloadUnit,
  resolveMotionConditionOperator,
  validationSchema,
  weekDays,
} from './RulesUtils';

const Rules = ({
  useruuid,
  measurementSystem,
  is24HourFormat,
  timezone,
  accessories,
  isLoadingAccessories,
  supportedCategories,
  headerActionsSlot,
}) => {
  const nextRuleIdRef = useRef(1);
  const ruleEditStateRef = useRef({});
  // Maps a rule id to its rendered card DOM node so we can scroll it into view when needed.
  const ruleCardRefsRef = useRef({});
  const [rules, setRules] = useState([]);
  const [deviceDropdownState, setDeviceDropdownState] = useState({});
  // Single, centrally-managed dialog shared by this component and every RuleFormCard.
  const [dialogConfig, setDialogConfig] = useState(null);

  const trans = useSelector(translator);

  const closeDialog = useCallback(() => setDialogConfig(null), []);
  const openDialog = useCallback((config) => setDialogConfig(config), []);

  // Smoothly scrolls the given rule's card into view once its expanded state has rendered.
  const scrollToRule = (ruleId) => ruleCardRefsRef.current[String(ruleId)]?.scrollIntoView();

  // TODO - Need to remove this and rely solely on the REDUX selector useInsightRuleSchemaMetadata
  const insightDefinitions = useSelector(getConfig(ConfigSourceKeys.insight_rules_schema));

  const {
    conditionTypes = {},
    conditionTypeOptions = [],
    conditionTypeOrder = [],
  } = useInsightRuleSchemaMetadata({ supportedCategories });
  const applyInsightRule = useApplyInsightRule();
  const deleteInsightRule = useDeleteInsightRule();
  const {
    data: uiJson = [],
    isLoading: isLoadingRules,
    isError: isErrorRules,
  } = useGetInsightRules({ useruuid });

  const isUiSchemaUnavailable = useMemo(() => {
    return (
      !insightDefinitions ||
      !Array.isArray(insightDefinitions.condition_types) ||
      insightDefinitions.condition_types.length === 0
    );
  }, [insightDefinitions]);

  useEffect(() => {
    nextRuleIdRef.current = 1;
    ruleEditStateRef.current = {};

    // Clean up any existing draft rules when the component unmounts, to prevent cross-user data leakage in the UI.
    // This will not delete any rules from the backend, only remove unsaved draft rules from the current UI
    return () => {
      setRules((previousRules) =>
        previousRules.filter((rule) => typeof rule.id === 'string' && !rule.id.startsWith('rule-'))
      );
    };
  }, [useruuid]);

  useEffect(() => {
    const existingRuleIds = new Set(rules.map((rule) => String(rule.id)));

    Object.keys(ruleEditStateRef.current).forEach((ruleId) => {
      if (!existingRuleIds.has(ruleId)) {
        delete ruleEditStateRef.current[ruleId];
      }
    });
  }, [rules]);

  const handleRuleEditStateChange = useCallback((ruleId, nextState) => {
    ruleEditStateRef.current[String(ruleId)] = {
      isDirty: Boolean(nextState?.isDirty),
    };
  }, []);

  const allowedProductClasses = useMemo(
    () =>
      isEmpty(supportedCategories)
        ? []
        : uniq(
            supportedCategories.flatMap(
              (category) => CATEGORY_PRODUCTCLASS_MAPPING[category] || [category]
            )
          ),
    [supportedCategories]
  );

  const validAccessories = useMemo(() => {
    return (
      accessories
        // Only include accessories with a valid devicetype (present and not 'NA') and paired or HC200
        .filter((accessory) => {
          const deviceType = get(accessory, ACCESSORIES_PROPERTIES.DEVICE_TYPE, '');
          const isValidDeviceType =
            typeof deviceType === 'string' &&
            deviceType.trim() &&
            deviceType.trim().toUpperCase() !== 'NA';
          const isPaired =
            get(accessory, ACCESSORIES_PROPERTIES.PAIRED_STATUS, '') ===
            DEVICE_CONNECTION_STATUS.PAIRED;
          const isHC200 =
            get(accessory, ACCESSORIES_PROPERTIES.MODEL_NUMBER, '') === HC200_MODEL_NUMBER;
          return (isValidDeviceType && isPaired) || isHC200;
        })

        // Further filter accessories to only include those whose type is in the supported categories list from config
        .filter((accessory) => {
          if (isEmpty(allowedProductClasses)) return false;
          const isHC200 =
            get(accessory, ACCESSORIES_PROPERTIES.MODEL_NUMBER, '') === HC200_MODEL_NUMBER;
          const productClass = isHC200
            ? WELLNESS_HUB_DEVICE_TYPE
            : get(accessory, ACCESSORIES_PROPERTIES.PRODUCT_CLASS, '');
          return productClass && allowedProductClasses.includes(productClass);
        })
    );
  }, [accessories, allowedProductClasses]);

  useEffect(() => {
    if (isLoadingAccessories || isLoadingRules) return;

    const derivedRules = formatUIJsonToComponentState(uiJson, validAccessories, measurementSystem);

    setRules((previousRules) => {
      const normalizeRuleName = (rule) =>
        String(rule?.values?.ruleName || '')
          .trim()
          .toLowerCase();
      const expandedByRuleId = previousRules.reduce((accumulator, rule) => {
        accumulator[rule.id] = Boolean(rule.expanded);
        return accumulator;
      }, {});

      const derivedRuleNames = new Set(
        derivedRules.map((rule) => normalizeRuleName(rule)).filter(Boolean)
      );

      const localDraftRules = previousRules.filter(
        (rule) =>
          typeof rule.id === 'string' &&
          rule.id.startsWith('rule-') &&
          !derivedRuleNames.has(normalizeRuleName(rule))
      );

      const mergedRules = [
        ...localDraftRules,
        ...derivedRules.map((rule) => ({
          ...rule,
          expanded: expandedByRuleId[rule.id] || false,
        })),
      ];

      return mergedRules.sort((leftRule, rightRule) => {
        const leftRuleName = normalizeRuleName(leftRule);
        const rightRuleName = normalizeRuleName(rightRule);

        if (!leftRuleName && !rightRuleName)
          return String(leftRule?.id || '').localeCompare(String(rightRule?.id || ''));

        if (!leftRuleName) return 1;

        if (!rightRuleName) return -1;

        return leftRuleName.localeCompare(rightRuleName);
      });
    });
  }, [measurementSystem, validAccessories, isLoadingAccessories, isLoadingRules, uiJson]);

  const getAccessoryType = useCallback((accessory) => {
    if (get(accessory, ACCESSORIES_PROPERTIES.MODEL_NUMBER, '') === HC200_MODEL_NUMBER) {
      return WELLNESS_HUB_DEVICE_TYPE;
    }

    const deviceType = get(accessory, ACCESSORIES_PROPERTIES.DEVICE_TYPE, '');

    if (
      typeof deviceType === 'string' &&
      deviceType.trim() &&
      deviceType.trim().toUpperCase() !== 'NA'
    ) {
      return deviceType.trim();
    }

    const productClass = get(accessory, ACCESSORIES_PROPERTIES.PRODUCT_CLASS, '');

    if (
      typeof productClass === 'string' &&
      productClass.trim() &&
      productClass.trim().toUpperCase() !== 'NA'
    ) {
      return productClass.trim();
    }

    return '';
  }, []);

  const getAccessoryLabel = useCallback(
    (accessory) => {
      const location = get(accessory, ACCESSORIES_PROPERTIES.LOCATION, '');
      const accessoryType = getAccessoryType(accessory);
      const normalizedAccessoryType =
        !accessoryType || accessoryType.toUpperCase() === 'NA'
          ? 'Unknown'
          : startCase(accessoryType);

      if (!location || location === '') {
        return normalizedAccessoryType;
      }

      return `${location}, ${normalizedAccessoryType}`;
    },
    [getAccessoryType]
  );

  const accessoryOptionsByType = useMemo(() => {
    const optionsByType = {};

    validAccessories.forEach((accessory) => {
      const accessoryType = getAccessoryType(accessory);

      if (!accessoryType) return;

      const optionValue =
        get(accessory, ACCESSORIES_PROPERTIES.DEVICE_UUID) ||
        get(accessory, ACCESSORIES_PROPERTIES.DEVICE_MAC_ADDRESS) ||
        get(accessory, ACCESSORIES_PROPERTIES.BASE_MAC_ADDRESS);

      if (!optionValue) return;

      const nextOption = { value: optionValue, label: getAccessoryLabel(accessory) };

      if (!optionsByType[accessoryType]) optionsByType[accessoryType] = [];

      const alreadyPresent = optionsByType[accessoryType].some(
        (existingOption) => existingOption.value === nextOption.value
      );
      if (!alreadyPresent) optionsByType[accessoryType].push(nextOption);
    });

    return optionsByType;
  }, [validAccessories, getAccessoryLabel, getAccessoryType]);

  const getDeviceOptionsForConditionType = useCallback(
    (conditionTypeKey) => {
      const conditionConfig = conditionTypes[conditionTypeKey] || {};
      const conditionDeviceTypes = (conditionConfig.devices || []).map(
        (deviceOption) => deviceOption.value
      );

      if (!conditionDeviceTypes.length) return [];

      const accessoryDeviceOptions = conditionDeviceTypes.flatMap(
        (deviceType) => accessoryOptionsByType[deviceType] || []
      );
      const uniqueAccessoryDeviceOptions = accessoryDeviceOptions.filter(
        (option, optionIndex, sourceOptions) =>
          sourceOptions.findIndex((candidateOption) => candidateOption.value === option.value) ===
          optionIndex
      );

      return uniqueAccessoryDeviceOptions;
    },
    [accessoryOptionsByType, conditionTypes]
  );

  const availableConditionTypeOptions = useMemo(
    () =>
      conditionTypeOptions.filter((opt) => !isEmpty(getDeviceOptionsForConditionType(opt.value))),
    [conditionTypeOptions, getDeviceOptionsForConditionType]
  );

  const defaultConditionType = useMemo(
    () => availableConditionTypeOptions[0]?.value || '',
    [availableConditionTypeOptions]
  );

  const normalizeDeviceValues = useCallback((deviceValue) => {
    if (Array.isArray(deviceValue)) {
      return deviceValue;
    }

    return deviceValue ? [deviceValue] : [];
  }, []);

  const getDefaultDeviceValuesForConditionType = () => [];

  const timezoneSelectOptions = useMemo(() => {
    const providedTimezone = typeof timezone === 'string' ? timezone.trim() : '';
    const fallbackTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const effectiveTimezone = providedTimezone || fallbackTimezone;

    return [{ value: effectiveTimezone, label: effectiveTimezone }];
  }, [timezone]);

  const hourlyTimeOptions = useMemo(
    () =>
      Array.from({ length: 24 }, (_, hour) => {
        const hourValue = String(hour).padStart(2, '0');
        const value = `${hourValue}:00`;

        if (is24HourFormat) return { value, label: value };

        const period = hour < 12 ? 'AM' : 'PM';
        const normalizedHour = hour % 12 || 12;

        return { value, label: `${normalizedHour}:00 ${period}` };
      }),
    [is24HourFormat]
  );

  const accessoryByIdentifier = useMemo(() => {
    const lookup = {};

    validAccessories.forEach((accessory) => {
      [
        get(accessory, ACCESSORIES_PROPERTIES.DEVICE_UUID),
        get(accessory, ACCESSORIES_PROPERTIES.DEVICE_MAC_ADDRESS),
        get(accessory, ACCESSORIES_PROPERTIES.BASE_MAC_ADDRESS),
      ]
        .filter(Boolean)
        .forEach((identifier) => {
          lookup[identifier] = accessory;
        });
    });

    return lookup;
  }, [validAccessories]);

  // TODO - Need to remove this and rely solely on the REDUX selector useInsightRuleSchemaMetadata
  const conditionTypeMetadata = useMemo(
    () =>
      (insightDefinitions?.condition_types || []).reduce((accumulator, conditionTypeConfig) => {
        if (!conditionTypeConfig?.id) {
          return accumulator;
        }

        const templates =
          conditionTypeConfig.condition_templates ||
          conditionTypeConfig.condition_template ||
          conditionTypeConfig.condition_types ||
          [];

        accumulator[conditionTypeConfig.id] = {
          devicetypes: conditionTypeConfig.devicetypes || [],
          templates,
        };

        return accumulator;
      }, {}),
    [insightDefinitions]
  );

  const findConditionTemplate = useCallback(
    (condition) => {
      const typeMetadata = conditionTypeMetadata[condition.type];
      const templates = typeMetadata?.templates || [];
      const conditionTypeConfig = conditionTypes[condition.type] || {};
      const isNumericCondition = conditionTypeConfig.valueType === 'number';

      if (!templates.length) {
        return null;
      }

      if (!isNumericCondition) {
        const matchedByThreshold = findTemplateByThreshold(templates, condition?.value);

        if (matchedByThreshold) {
          return matchedByThreshold;
        }

        const matchedByName = findTemplateByName(templates, condition?.condition_template_name);

        if (matchedByName) {
          return matchedByName;
        }
      }

      return (
        templates.find((template) => {
          if (template?.operator !== condition.operator) {
            return false;
          }

          const templateThreshold = template?.threshold;

          if (
            templateThreshold &&
            typeof templateThreshold === 'object' &&
            templateThreshold.type === 'numeric'
          ) {
            return true;
          }

          return String(templateThreshold ?? '') === String(condition.value ?? '');
        }) || null
      );
    },
    [conditionTypeMetadata, conditionTypes]
  );

  const buildSubmitPayload = useCallback(
    (values) => {
      let updatedValues = cloneDeep(values);
      let daysOfWeek = weekDays
        .filter((day) => updatedValues.days?.[day.key])
        .map((day) => day.key);

      // if no devices selected for a condition, default to all devices for that condition type
      updatedValues.conditions = (updatedValues.conditions || []).map((condition) => {
        if (Array.isArray(condition.device) && condition.device.length > 0) return condition;
        const allDeviceIds = (getDeviceOptionsForConditionType(condition.type) || []).map(
          (d) => d.value
        );
        return { ...condition, device: allDeviceIds };
      });

      const payloadConditions = (updatedValues.conditions || []).map((condition) => {
        const conditionTemplate = findConditionTemplate(condition);
        const existingTemplateName = condition?.condition_template_name;
        const selectedDevices = normalizeDeviceValues(condition.device);
        const typeMetadata = conditionTypeMetadata[condition.type] || {};
        const conditionUnit = getConditionPayloadUnit(
          condition.type,
          measurementSystem,
          conditionTypes
        );
        let resolvedOperator = conditionTemplate?.operator || condition.operator;

        if (condition.type === 'motion') {
          resolvedOperator = resolveMotionConditionOperator({
            value: condition?.value,
            templateName: conditionTemplate?.name || existingTemplateName,
            fallbackOperator: resolvedOperator,
          });
        }

        // Extract device types from selected accessories
        const selectedDeviceTypes = selectedDevices
          .map((identifier) => {
            const accessory = accessoryByIdentifier[identifier];
            return getAccessoryType(accessory);
          })
          .filter(Boolean);

        const uniqueDeviceTypes = [...new Set(selectedDeviceTypes)];

        const conditionPayload = {
          id: condition.type,
          devicetypes:
            uniqueDeviceTypes.length > 0 ? uniqueDeviceTypes : typeMetadata.devicetypes || [],
          operator: resolvedOperator,
          value: String(condition.value ?? ''),
          ...(conditionUnit ? { unit: conditionUnit } : {}),
          devices: selectedDevices.map((identifier) => {
            const accessory = accessoryByIdentifier[identifier];
            return {
              macaddress:
                get(accessory, ACCESSORIES_PROPERTIES.DEVICE_MAC_ADDRESS) ||
                get(accessory, ACCESSORIES_PROPERTIES.BASE_MAC_ADDRESS) ||
                '',
              deviceuuid: get(accessory, ACCESSORIES_PROPERTIES.DEVICE_UUID, ''),
            };
          }),
        };

        if (conditionTemplate?.name || existingTemplateName) {
          conditionPayload.condition_template_name =
            conditionTemplate?.name || existingTemplateName;
        }

        return conditionPayload;
      });

      return {
        updatedValues,
        submitPayload: {
          rulename: updatedValues.ruleName || '',
          category: updatedValues.category || '',
          enabled: Boolean(updatedValues.enabled),
          notify: Boolean(updatedValues.notify),
          days_of_week: daysOfWeek,
          start_time: updatedValues.startTime || '',
          end_time: updatedValues.endTime || '',
          timezone:
            updatedValues.startTimezone ||
            updatedValues.endTimezone ||
            timezoneSelectOptions[0]?.value ||
            '',
          conditions: payloadConditions,
        },
      };
    },
    [
      accessoryByIdentifier,
      conditionTypes,
      conditionTypeMetadata,
      findConditionTemplate,
      getAccessoryType,
      getDeviceOptionsForConditionType,
      measurementSystem,
      normalizeDeviceValues,
      timezoneSelectOptions,
    ]
  );

  const ruleValidationSchema = useMemo(
    () =>
      validationSchema(
        conditionTypes,
        availableConditionTypeOptions,
        getDeviceOptionsForConditionType,
        trans
      ),
    [conditionTypes, availableConditionTypeOptions, getDeviceOptionsForConditionType, trans]
  );

  const validateRuleForm = useCallback(
    (values) => {
      try {
        ruleValidationSchema.validateSync(values, { abortEarly: false });
        return {};
      } catch (validationError) {
        if (!(validationError instanceof Yup.ValidationError)) return {};
        const nextErrors = {};
        if (!validationError.inner?.length) {
          if (validationError.path) {
            set(nextErrors, validationError.path, validationError.message);
          }
          return nextErrors;
        }
        validationError.inner.forEach((innerError) => {
          if (!innerError.path || get(nextErrors, innerError.path)) return;
          set(nextErrors, innerError.path, innerError.message);
        });
        return nextErrors;
      }
    },
    [ruleValidationSchema]
  );

  const handleSubmit = (ruleId, values, { setSubmitting, resetForm }) => {
    const { submitPayload, updatedValues } = buildSubmitPayload(values);
    const isExistingRule = ruleId && !ruleId.startsWith('rule-');
    if (isExistingRule) {
      submitPayload.id = ruleId;
    }
    const rulename = submitPayload?.rulename;

    const successCallback = (responseData) => {
      setSubmitting(false);
      // Get the actual rule ID from the response if available
      const actualRuleId = responseData?.id || ruleId;
      const normalizedRuleValues = {
        ...values,
        ...updatedValues,
        conditions: (updatedValues.conditions || []).map((condition) => ({
          ...condition,
          device: normalizeDeviceValues(condition.device),
        })),
        days: {
          ...(updatedValues.days || {}),
        },
      };

      resetForm({ values: normalizedRuleValues });

      message.success(
        <MessageToastContent>
          {trans(isExistingRule ? 'RULEBUILDER_UPDATE_SUCCESS' : 'RULEBUILDER_CREATE_SUCCESS')}
          {rulename ? (
            <>
              {': '}
              <span>{rulename}</span>
            </>
          ) : null}
        </MessageToastContent>
      );

      setRules((previousRules) => {
        // Match by the original ID (which may be a temp ID for new rules)
        if (!previousRules.find((r) => r.id === ruleId)) {
          console.warn('[RuleSubmit] Rule not found with id:', ruleId);
          return previousRules;
        }

        return previousRules.map((existingRule) => {
          if (existingRule.id !== ruleId) {
            return existingRule;
          }

          // Return updated rule, using the actual ID from the response
          return {
            ...existingRule,
            id: actualRuleId,
            expanded: false, // collapse the rule after submission
            values: normalizedRuleValues,
          };
        });
      });
    };

    const errorCallback = (err) => {
      setSubmitting(false);
      console.error('Error applying insight rule:', err);
      message.error(
        <MessageToastContent>
          {trans(isExistingRule ? 'RULEBUILDER_UPDATE_ERROR' : 'RULEBUILDER_CREATE_ERROR')}
          {rulename ? (
            <>
              {': '}
              <span>{rulename}</span>
            </>
          ) : null}
        </MessageToastContent>
      );
    };

    applyInsightRule.mutate({
      requestBody: submitPayload,
      successCallback,
      errorCallback,
      useruuid: useruuid,
    });
  };

  const toggleRule = (ruleId) => {
    // Block collapsing a different expanded rule that still has unsaved edits.
    const dirtyExpandedRule = rules.find(
      (rule) =>
        rule.id !== ruleId &&
        rule.expanded &&
        Boolean(ruleEditStateRef.current[String(rule.id)]?.isDirty)
    );

    if (dirtyExpandedRule) {
      openDialog({
        title: trans('RULEBUILDER_ACTION_REQUIRED'),
        description: trans('RULEBUILDER_SAVE_EDITS_BEFORE_COLLAPSE'),
        confirmLabel: trans('OK'),
        confirmIcon: null,
        hideCancel: true,
        onConfirm: () => scrollToRule(dirtyExpandedRule.id),
      });
      return;
    }

    setRules((previousRules) =>
      previousRules.map((rule) => ({
        ...rule,
        expanded: rule.id === ruleId ? !rule.expanded : false,
      }))
    );
  };

  const addRule = () => {
    // Prevent adding a new rule if there's an existing unsaved draft rule
    const unsavedDraftRule = rules.find(
      (rule) => typeof rule.id === 'string' && rule.id.startsWith('rule-')
    );
    if (unsavedDraftRule) {
      openDialog({
        title: trans('RULEBUILDER_ACTION_REQUIRED'),
        description: trans('RULEBUILDER_SAVE_CURRENT_RULE_FIRST'),
        confirmLabel: trans('OK'),
        confirmIcon: null,
        hideCancel: true,
        onConfirm: () => {
          setRules((previousRules) =>
            previousRules.map((rule) =>
              rule.id === unsavedDraftRule.id ? { ...rule, expanded: true } : rule
            )
          );
          scrollToRule(unsavedDraftRule.id);
        },
      });
      return;
    }

    // Prevent adding a new rule if there are unsaved edits in any rule.
    const editedRule = rules.find((rule) => {
      const editState = ruleEditStateRef.current[String(rule.id)];
      return Boolean(editState?.isDirty);
    });

    if (editedRule) {
      openDialog({
        title: trans('RULEBUILDER_ACTION_REQUIRED'),
        description: trans('RULEBUILDER_SAVE_CURRENT_EDITS_FIRST'),
        confirmLabel: trans('OK'),
        confirmIcon: null,
        hideCancel: true,
        onConfirm: () => {
          setRules((previousRules) =>
            previousRules.map((rule) =>
              rule.id === editedRule.id ? { ...rule, expanded: true } : rule
            )
          );
          scrollToRule(editedRule.id);
        },
      });
      return;
    }

    const defaultCondition = createCondition(
      defaultConditionType,
      conditionTypes,
      conditionTypeOrder
    );
    defaultCondition.device = getDefaultDeviceValuesForConditionType(defaultCondition.type);

    setRules((previousRules) => {
      const existingRuleIds = new Set(previousRules.map((rule) => rule.id));
      let nextRuleId = `rule-${nextRuleIdRef.current}`;

      while (existingRuleIds.has(nextRuleId)) {
        nextRuleIdRef.current += 1;
        nextRuleId = `rule-${nextRuleIdRef.current}`;
      }

      nextRuleIdRef.current += 1;

      return [
        {
          id: nextRuleId,
          expanded: true,
          values: createRuleInitialValues(
            {
              ruleName: '',
              enabled: true,
              conditions: [defaultCondition],
              startTimezone: timezoneSelectOptions[0]?.value || '',
              endTimezone: timezoneSelectOptions[0]?.value || '',
            },
            conditionTypes,
            conditionTypeOrder
          ),
        },
        ...previousRules,
      ];
    });
  };

  const handleDelete = (ruleId, resetForm) => {
    const removeRuleLocally = () => {
      setRules((previousRules) => {
        if (previousRules.length === 1) {
          resetForm();
          return [];
        }
        return previousRules.filter((rule) => rule.id !== ruleId);
      });
    };

    const isExistingRule = ruleId && !ruleId.startsWith('rule-');
    const rulename = rules.find((rule) => rule.id === ruleId)?.values?.ruleName || '';

    if (isExistingRule) {
      deleteInsightRule.mutate({
        useruuid: useruuid,
        ruleuuid: ruleId,
        successCallback: () => {
          removeRuleLocally();
          message.success(
            <MessageToastContent>
              {trans('RULEBUILDER_DELETE_SUCCESS')}
              {rulename ? (
                <>
                  {': '}
                  <span>{rulename}</span>
                </>
              ) : null}
            </MessageToastContent>
          );
        },
        errorCallback: (err) => {
          console.error('Error deleting insight rule:', err);
          message.error(
            <MessageToastContent>
              {trans('RULEBUILDER_DELETE_ERROR')}
              {rulename ? (
                <>
                  {': '}
                  <span>{rulename}</span>
                </>
              ) : null}
            </MessageToastContent>
          );
        },
      });
      return;
    }

    removeRuleLocally();
  };

  if (isUiSchemaUnavailable) return <Error message={trans('RULES_SCHEMA_UNAVAILABLE')} />;

  if (isLoadingAccessories || isLoadingRules) {
    return (
      <SkeletonWrapper>
        <Skeleton active round title={{ width: '50%' }} paragraph={{ rows: 1, width: '100%' }} />
      </SkeletonWrapper>
    );
  }

  return (
    <>
      {headerActionsSlot &&
        createPortal(
          <AddRuleButton onClick={addRule}>
            <DashboardIconAdd /> {trans('RULEBUILDER_ADD_RULE')}
          </AddRuleButton>,
          headerActionsSlot
        )}

      <RulesListContainer>
        {rules?.length === 0 ? (
          isErrorRules ? (
            <Error message={trans('RULES_FETCHING_ERROR')} />
          ) : (
            <NoRulesDiv>
              <InfoIcon className="no-rules-icon" />
              <span className="no-rules">{trans('NO_RULES_ESTABLISHED')}</span>
            </NoRulesDiv>
          )
        ) : (
          rules.map((rule) => (
            <RuleFormCard
              key={rule.id}
              rule={rule}
              cardRef={(node) => {
                if (node) {
                  ruleCardRefsRef.current[String(rule.id)] = node;
                } else {
                  delete ruleCardRefsRef.current[String(rule.id)];
                }
              }}
              handleSubmit={handleSubmit}
              validateRuleForm={validateRuleForm}
              toggleRule={toggleRule}
              setRules={setRules}
              handleDelete={handleDelete}
              availableConditionTypeOptions={availableConditionTypeOptions}
              defaultConditionType={defaultConditionType}
              getDefaultDeviceValuesForConditionType={getDefaultDeviceValuesForConditionType}
              getDeviceOptionsForConditionType={getDeviceOptionsForConditionType}
              normalizeDeviceValues={normalizeDeviceValues}
              deviceDropdownState={deviceDropdownState}
              setDeviceDropdownState={setDeviceDropdownState}
              timezoneSelectOptions={timezoneSelectOptions}
              hourlyTimeOptions={hourlyTimeOptions}
              is24HourFormat={is24HourFormat}
              measurementSystem={measurementSystem}
              onEditStateChange={handleRuleEditStateChange}
              supportedCategories={supportedCategories}
              openDialog={openDialog}
              scrollToRule={scrollToRule}
            />
          ))
        )}
      </RulesListContainer>

      <CustomDialogBox
        isModalOpen={Boolean(dialogConfig)}
        modalHeader={dialogConfig?.title || ''}
        description={dialogConfig?.description || null}
        showDeleteDescrPath={dialogConfig?.showDeleteDescrPath || false}
        userFullName={dialogConfig?.userFullName || ''}
        confirmLabel={dialogConfig?.confirmLabel || ''}
        cancelLabel={dialogConfig?.cancelLabel || ''}
        confirmIcon={dialogConfig?.confirmIcon}
        hideCancel={dialogConfig?.hideCancel || false}
        onConfirm={() => {
          dialogConfig?.onConfirm?.();
          closeDialog();
        }}
        onCancel={() => {
          dialogConfig?.onCancel?.();
          closeDialog();
        }}
      />
    </>
  );
};

Rules.propTypes = {
  useruuid: PropTypes.string,
  measurementSystem: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  timezone: PropTypes.string,
  accessories: PropTypes.arrayOf(PropTypes.object),
  isLoadingAccessories: PropTypes.bool,
  supportedCategories: PropTypes.array,
  headerActionsSlot: PropTypes.object,
};

export default Rules;
